/**
 * In-memory store for the media-encryption master key.
 *
 * The key is provisioned by the server in the `login` control response (see
 * docs/MEDIA_ENCRYPTION.md §4) as the `mediaKey` server parameter: base64 of 32
 * random bytes.
 *
 * NOTE ON PERSISTENCE: the key is deliberately kept in memory only. It is never
 * written to localStorage/sessionStorage/IndexedDB because those are readable by
 * any script running on the origin, so a single XSS bug would leak the key that
 * decrypts every attachment the user can see. The key is cheap to re-obtain —
 * it arrives with every login — so there is nothing to gain from caching it.
 * (iOS caches it in the Keychain instead, which is a materially different
 * security boundary; the browser has no equivalent.)
 *
 * Everything here degrades gracefully: if the server does not send the
 * parameter, `hasKey()` is false, the send path uploads plaintext exactly as it
 * did before this feature, and legacy media keeps rendering.
 */

import { KEY_SIZE, fromBase64 } from './media-crypto.js';

class MediaKeyStore {
  constructor() {
    this._key = null;
    this._keyId = null;
  }

  /**
   * Read the master key from the server parameters of a logged-in Tinode
   * instance. Safe to call more than once; safe to call when the server does not
   * support media encryption. Never throws.
   *
   * @param {Tinode} tinode - connected and authenticated Tinode instance.
   * @returns {boolean} true if a usable key was installed.
   */
  setFromServerParams(tinode) {
    this.clear();

    if (!tinode || typeof tinode.getServerParam != 'function') {
      return false;
    }

    let b64;
    try {
      b64 = tinode.getServerParam('mediaKey');
    } catch (err) {
      // Not connected/authenticated yet. Not an error.
      return false;
    }

    if (!b64 || typeof b64 != 'string') {
      // Server does not provide media encryption. Expected on older servers.
      return false;
    }

    let bytes = null;
    try {
      bytes = fromBase64(b64);
    } catch (err) {
      console.warn("Media encryption: 'mediaKey' is not valid base64; encryption disabled.");
      return false;
    }

    if (!bytes || bytes.length != KEY_SIZE) {
      console.warn(`Media encryption: 'mediaKey' must decode to ${KEY_SIZE} bytes, got ` +
        `${bytes ? bytes.length : 0}; encryption disabled.`);
      return false;
    }

    this._key = bytes;

    let kid = null;
    try {
      kid = tinode.getServerParam('mediaKeyId');
    } catch (err) {
      kid = null;
    }
    this._keyId = (typeof kid == 'string' && kid) ? kid : null;

    return true;
  }

  /** The 32-byte master key, or null when unavailable. */
  getKey() {
    return this._key;
  }

  /** Server-provided key id, or null. The id is recomputed locally when needed. */
  getKeyId() {
    return this._keyId;
  }

  /** True when a valid master key is loaded. */
  hasKey() {
    return this._key != null;
  }

  /** Forget the key. Called on logout. */
  clear() {
    this._key = null;
    this._keyId = null;
  }
}

/** Process-wide singleton. */
export const mediaKeyStore = new MediaKeyStore();

export default mediaKeyStore;
